import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { logEventToBackEnd } from '../global/globalSlice';
import { sumBy } from 'lodash';
import { uipApiInstance } from '../../services';
import {
    AdvisorConsoleStore,
    AdvisorConsoleStoreFilters,
    GetAdvisorConsoleProposalsPayload,
    GetProposalsResponse,
    DeletePlanPayload
} from './types';
import { GoalTypeEnum, GoalTypeTitlemapping } from '../../common/types/clients-entity';
import { AdminTableItem } from 'components/molecules/AdminTable';
import {
    calculateEquityFixedIncome,
    filterDataItems,
    getActionRequiredGoals,
    getItemsByPage,
    getNumberOfPages
} from './utils';
import _uniqBy from 'lodash/uniqBy';
import _sortBy from 'lodash/sortBy';
import {
    priorityOptions,
    riskProfileOptions,
    statusOptions,
    typeOptions,
    STATUSVARIANTS,
    FEATURE_NAMES
} from '../../common/constants';
import dayjs from 'dayjs';
import { checkFFEnabled, formatCurrency, getClientJourneyFlowType } from '../../utils';

export const getAdvisorConsoleProposals = createAsyncThunk(
    'advisorConsole/getproposals',
    async (
        {
            tenant,
            page = 1,
            itemsPerPage,
            proposalPaginationSearchFFEnabled = false,
            onload = false
        }: GetAdvisorConsoleProposalsPayload,
        { dispatch, rejectWithValue, getState }
    ) => {
        try {
            const params = new URLSearchParams({
                tenant: tenant
            });
            if (proposalPaginationSearchFFEnabled) {
                if (!onload) {
                    dispatch(advisorConsoleActions.setFromFilters(true));
                }
                params.append('page', String(page));
                params.append('page_size', String(itemsPerPage));
                const state: any = getState();
                const currentFilters = state.advisorConsole.filters;

                if (currentFilters.client && currentFilters.client.label !== '') {
                    params.append('client_search_term', currentFilters.client.label);
                }

                if (currentFilters.status) params.append('status', currentFilters.status);
                if (currentFilters.riskProfile) params.append('risk_profile', currentFilters.riskProfile);
                if (currentFilters.type) params.append('goal_type', currentFilters.type);
                if (currentFilters.priority) params.append('goal_priority', currentFilters.priority);
                if (currentFilters.advisor && currentFilters.advisor !== '') {
                    params.append('advisor_search_term', currentFilters.advisor);
                }

                const isOffsetEnabled = checkFFEnabled(FEATURE_NAMES['AEX-3245_PAGINATION_WITH_TIMESTAMP_SORTING']);
                if (!isOffsetEnabled) {
                    const nextCursor = state.advisorConsole.pagination.nextCursor;
                    if (nextCursor && page > 1) {
                        params.append('cursor_id', nextCursor);
                    }
                }
            }

            const { data: apiResponse } = await uipApiInstance<GetProposalsResponse>({
                method: 'GET',
                url: `/api/advice/getproposals?${params.toString()}`
            });
            const isFlow2 = getClientJourneyFlowType(tenant) === 'flow2';

            const fullState: any = getState();
            let totalProposals = 0;
            if (params.has('cursor_id')) {
                totalProposals = fullState?.advisorConsole?.pagination?.totalItems;
            } else {
                totalProposals = apiResponse?.total_proposals ?? 0;
            }
            const dataItems: AdminTableItem[] =
                apiResponse?.data?.map(
                    ({
                        clientFirstName,
                        clientLastName,
                        advisorName,
                        form_fields = [],
                        global,
                        _id,
                        riskProfile,
                        response,
                        request,
                        lastUpdateDate,
                        participant_id,
                        notifications = []
                    }: any) => {
                        const isPlanIncomplete =
                            (global?.status ?? STATUSVARIANTS.INCOMPLETE) === STATUSVARIANTS.INCOMPLETE;
                        const subItems =
                            form_fields?.map((formField: any) => {
                                const { data, id, goalId } = formField;
                                const goal = (GoalTypeTitlemapping as { [key: string]: GoalTypeEnum })[
                                    data.wealthGoalType!
                                ];

                                const goal_data = request.filter(
                                    (itemRequest: any) => goalId === itemRequest?.goalId
                                )[0];

                                return {
                                    goal,
                                    portfolioValue: isPlanIncomplete
                                        ? undefined
                                        : isFlow2
                                        ? null
                                        : sumBy(goal_data?.accounts, 'amount') || 0,
                                    probabilityofSuccess: data.probabilityOfSuccess,
                                    id: [_id, id, goalId].filter(Boolean).join('_'),
                                    goalPriority: data?.goalPriority || '',
                                    goalType: data?.goalType,
                                    equityFixedIncome:
                                        data?.equityFixedIncome ||
                                        calculateEquityFixedIncome({
                                            portfolios: fullState?.global?.portfolio,
                                            recommendedPortfolioId:
                                                response?.[0]?.response?.body?.analysisReport?.recommendedPortfolioId
                                        })
                                };
                            }) || [];

                        const numberofGoals = ~~form_fields?.length;

                        let lastUpdated = '';
                        const proposalLastUpdateDate = [lastUpdateDate, global?.lastUpdateDate].find(
                            (date) => date && dayjs(date).isValid()
                        );
                        if (proposalLastUpdateDate) {
                            lastUpdated = dayjs(proposalLastUpdateDate).format('MM/DD/YYYY');
                        }
                        const portfolioValue = isPlanIncomplete
                            ? undefined
                            : isFlow2
                            ? global?.portfolioValue
                            : subItems.reduce((acc: number, { portfolioValue }: { portfolioValue: number }) => {
                                  if (portfolioValue) {
                                      return acc + portfolioValue;
                                  }
                                  return acc;
                              }, 0);
                        // AEX-3218: goals in this plan that currently need advisor review, joined with
                        // their goal details for the Review Goals modal.
                        const actionRequiredGoals = getActionRequiredGoals(notifications, form_fields);
                        return {
                            id: _id,
                            clientGoalName: [clientFirstName, clientLastName].filter(Boolean).join(' '),
                            status: global?.status ?? STATUSVARIANTS.INCOMPLETE,
                            advisorName: advisorName ?? '',
                            probabilityOfSuccess: undefined,
                            equityFixedIncome: global?.equityFixedIncome,
                            // portfolioApproach,
                            riskGoalPriority: riskProfile,
                            portfolioValue: (portfolioValue ? formatCurrency(portfolioValue) : undefined) as any,
                            numberofGoals,
                            subItems,
                            lastUpdated,
                            enablePlanDetails: global?.enablePlanDetails,
                            enablePDFGenerate: global?.enablePDFGenerate,
                            participantId: participant_id,
                            // AEX-3218/AEX-3570: goals needing review + their details. The presentation
                            // layer gates display on the FF + flow2.
                            actionRequiredGoals,
                            actionRequiredGoalCount: actionRequiredGoals.length
                        };
                    }
                ) || [];

            // const itemsPerPage = 10;
            const DEFAULT_ITEMS_PER_PAGE = 10;

            let pagination: AdvisorConsoleStore['pagination'];
            let items: AdminTableItem[] = [];
            if (proposalPaginationSearchFFEnabled) {
                pagination = {
                    page: page || 1,
                    totalPages: getNumberOfPages(
                        totalProposals || dataItems.length,
                        itemsPerPage || DEFAULT_ITEMS_PER_PAGE
                    ),
                    itemsPerPage: itemsPerPage || DEFAULT_ITEMS_PER_PAGE,
                    totalItems: totalProposals || dataItems.length,
                    nextCursor: apiResponse?.nextCursor,
                    nextPageNumber: (page || 1) + 1
                };
                items = [...dataItems];
            } else {
                pagination = {
                    page: 1,
                    totalPages: getNumberOfPages(dataItems.length, DEFAULT_ITEMS_PER_PAGE),
                    itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
                    totalItems: dataItems.length
                };
                items = getItemsByPage({
                    dataItems,
                    itemsPerPage: itemsPerPage || DEFAULT_ITEMS_PER_PAGE,
                    page: page || 1
                });
            }

            const clients = _sortBy(
                _uniqBy(
                    dataItems
                        ?.filter(({ clientGoalName }) => !!clientGoalName)
                        ?.map(({ clientGoalName }) => {
                            const name = clientGoalName?.toUpperCase();

                            return {
                                value: name,
                                label: clientGoalName
                            };
                        }),
                    'value'
                ),
                'label'
            );

            const keyValueRequest = apiResponse?.data?.reduce((acc: Record<string, any>, { _id, ...rest }: any) => {
                acc[_id] = { ...rest, _id };
                return acc;
            }, {});

            return {
                dataItems,
                items,
                pagination,
                clients,
                keyValueRequest,
                fromFilters: false
            };
        } catch (err: any) {
            console.log(err);
            dispatch(logEventToBackEnd('GET_PROPOSALS_API_ERROR'));
            if (err?.code === 'ERR_BAD_REQUEST') {
                return {
                    dataItems: [],
                    items: [],
                    pagination: {
                        page: 1,
                        totalPages: 1,
                        itemsPerPage: 0,
                        totalItems: 0,
                        nextCursor: '',
                        nextPageNumber: 0
                    },
                    clients: [],
                    keyValueRequest: {}
                };
            }
            return rejectWithValue(err);
        }
    }
);

export const getDocsByProposalIds = createAsyncThunk(
    'advisorConsole/getdocsbyproposalids',
    async ({ tenant, proposalIds }: { tenant: string; proposalIds: string[] }, { dispatch, rejectWithValue }) => {
        const data = {
            from_tenant: tenant,
            proposalIds: proposalIds
        };
        try {
            const response = await uipApiInstance({
                method: 'POST',
                url: '/api/advice/getdocsbyproposalids',
                data: data
            });
            return response.data;
        } catch (err) {
            console.log(err);
            dispatch(logEventToBackEnd('GET_ALL_DOCS_ERROR'));
            return rejectWithValue(err);
        }
    }
);

export const deletePlan = createAsyncThunk(
    'advisorConsole/deleteclientprofile',
    async ({ tenant, planId }: DeletePlanPayload, { dispatch, rejectWithValue, getState }) => {
        try {
            const response = await uipApiInstance({
                method: 'POST',
                url: `/api/advice/deleteclientprofile/${planId}?tenant=${tenant}`,
                withCredentials: false
            });
            if (response.data.success) {
                const proposalPaginationSearchFFEnabled =
                    checkFFEnabled(FEATURE_NAMES['PROPOSALS_PAGINATION_SEARCH_FE_AEX-2382']) || false;
                const state: any = getState();
                const { page, itemsPerPage } = state.advisorConsole.pagination;
                dispatch(
                    getAdvisorConsoleProposals({
                        tenant,
                        page,
                        itemsPerPage,
                        proposalPaginationSearchFFEnabled
                    })
                );
            }
        } catch (err) {
            console.log(err);
            dispatch(logEventToBackEnd('DELETE_PLAN_ERROR'));
            return rejectWithValue(err);
        }
    }
);

// The default status filter depends on whether the tenant is a flow2 tenant. Flow2 tenants default
// to "Complete"; all others default to "All". The isMultiPortfolioTenant parameter is retained for
// backward compatibility with callers but does not affect the result (see isFlow2MultiPortfolioTenant
// logic which reduces to just isFlow2 by boolean absorption: A || (A && B) = A).
export const getDefaultStatusFilter = (isFlow2: boolean, isMultiPortfolioTenant: boolean) =>
    isFlow2 ? statusOptions[2]?.label : statusOptions[0]?.value;

const INITIAL_STATE: AdvisorConsoleStore = {
    loading: false,
    pagination: {
        page: 1,
        totalPages: 1,
        itemsPerPage: 10,
        totalItems: 0,
        nextCursor: '',
        nextPageNumber: 0
    },
    items: [],
    dataItems: [],
    clients: [],
    error: false,
    filters: {
        status: getDefaultStatusFilter(false, false),
        riskProfile: riskProfileOptions[0]?.value,
        type: typeOptions[0]?.value,
        priority: priorityOptions[0]?.value,
        client: { value: '', label: '' }
    },
    hasFilters: false,
    keyValueRequest: {},
    planDeletedSuccess: false,
    docsByProposalId: null,
    allDocsLoading: false
};

const advisorConsole = createSlice({
    name: 'advisorConsole',
    initialState: INITIAL_STATE,
    reducers: {
        changePage: (state, action: PayloadAction<{ page: number; proposalPaginationSearchFFEnabled: boolean }>) => {
            const {
                pagination: { itemsPerPage }
            } = state;

            const dataItems = filterDataItems({
                dataItems: state.dataItems,
                filters: state.filters
            });

            const page = action.payload.page;

            state.pagination.page = page;

            if (!action.payload.proposalPaginationSearchFFEnabled) {
                state.items = getItemsByPage({
                    dataItems,
                    itemsPerPage,
                    page
                });
            }
        },
        changeItemsPerPage: (
            state,
            action: PayloadAction<{ itemsPerPage: number; proposalPaginationSearchFFEnabled: boolean }>
        ) => {
            const page = 1; // force back to page 1 to prevent bugs
            const itemsPerPage = action.payload.itemsPerPage;

            const dataItems = filterDataItems({
                dataItems: state.dataItems,
                filters: state.filters
            });

            state.pagination = {
                ...state.pagination,
                page,
                totalPages: getNumberOfPages(dataItems.length, itemsPerPage),
                itemsPerPage
            };

            if (!action.payload.proposalPaginationSearchFFEnabled) {
                const items = getItemsByPage({
                    dataItems,
                    itemsPerPage,
                    page
                });

                state.items = items;
            }
        },
        setFilters: (
            state,
            action: PayloadAction<{ filters: AdvisorConsoleStoreFilters; proposalPaginationSearchFFEnabled: boolean }>
        ) => {
            const page = 1; // force back to page 1 to prevent bugs

            /**
             *
             *
             *
             *
             */
            /// TODO: FINISH FILTERS VALIDAITONS

            const filteredDataItems = filterDataItems({
                dataItems: state.dataItems,
                filters: action.payload.filters
            });

            if (!action.payload.proposalPaginationSearchFFEnabled) {
                const items = getItemsByPage({
                    dataItems: filteredDataItems,
                    itemsPerPage: state.pagination.itemsPerPage,
                    page
                });

                state.items = items;
            }

            state.pagination = {
                ...state.pagination,
                page,
                totalPages: getNumberOfPages(filteredDataItems.length, state.pagination.itemsPerPage),
                totalItems: filteredDataItems.length
            };

            state.filters = action.payload.filters;

            state.hasFilters = true;
        },
        // Seed the default status filter when the console is untouched (e.g. flow2 tenants default to
        // "Complete" on first load). Deliberately does NOT set hasFilters, so this is treated as the
        // default rather than a user-applied filter and won't block later default re-seeding.
        setDefaultStatusFilter: (state, action: PayloadAction<string | undefined>) => {
            state.filters.status = action.payload;
        },
        setFromFilters: (state, action: PayloadAction<boolean>) => {
            state.fromFilters = action.payload;
        },
        resetAdviceConsoleReducer: () => INITIAL_STATE,
        clearPlanDeletedSuccess: (state) => {
            state.planDeletedSuccess = false;
        },
        setTenantRefresh: (state, action: PayloadAction<any>) => {
            state.dropDownTenantRefresh = action.payload;
        },
        resetForTenantSwitch: (state, action: PayloadAction<{ isFlow2: boolean; isMultiPortfolioTenant: boolean }>) => {
            // Treat a tenant switch as a full advisor-console reset so the next
            // visit to Clients always starts clean for the newly selected tenant.
            state.filters = { ...INITIAL_STATE.filters };
            // isFlow2 is computed by the caller and passed in, because the flow-type
            // lookup reads the store (via feature flags) and reducers must stay pure.
            state.filters = {
                ...INITIAL_STATE.filters,
                status: getDefaultStatusFilter(
                    action.payload.isFlow2 ?? false,
                    action.payload.isMultiPortfolioTenant ?? false
                )
            };
            state.hasFilters = false;
            state.fromFilters = false;
            state.currentRequestId = undefined;
            state.loading = false;
            state.pagination = {
                ...state.pagination,
                page: 1,
                totalPages: 1,
                totalItems: 0,
                nextCursor: '',
                nextPageNumber: 0
            };
        }
    },
    extraReducers: (builder) => {
        builder.addCase(getAdvisorConsoleProposals.pending, (state, action) => {
            state.currentRequestId = action.meta.requestId;
            state.loading = true;
        }),
            builder.addCase(getAdvisorConsoleProposals.fulfilled, (state, action) => {
                if (state.currentRequestId !== action.meta.requestId) return;

                const { dataItems, items, pagination, clients, keyValueRequest } = action.payload;

                state.dataItems = dataItems;
                state.items = items;
                state.pagination = pagination;
                state.clients = clients;
                state.keyValueRequest = keyValueRequest;
                state.loading = false;
                state.error = false;
                state.fromFilters = false;
                state.currentRequestId = undefined;
            }),
            builder.addCase(getAdvisorConsoleProposals.rejected, (state, action) => {
                if (state.currentRequestId !== action.meta.requestId) return;

                state.loading = false;
                state.error = true;
                state.currentRequestId = undefined;
            });
        builder.addCase(deletePlan.fulfilled, (state) => {
            state.planDeletedSuccess = true;
        });
        builder.addCase(getDocsByProposalIds.pending, (state) => {
            state.allDocsLoading = true;
        });
        builder.addCase(getDocsByProposalIds.fulfilled, (state, action) => {
            state.docsByProposalId = action.payload ?? [];
            state.allDocsLoading = false;
        });
        builder.addCase(getDocsByProposalIds.rejected, (state) => {
            state.allDocsLoading = false;
        });
    }
});

export const { actions: advisorConsoleActions } = advisorConsole;

export default advisorConsole.reducer;
